import {ActiveUserStore} from '@active-user-management';
import {inject, Injectable, Injector} from '@angular/core';
import {AuthService, AuthStore} from '@auth/data-access';
import {BackendService, CycleListType, ICycleQueryRequest, ICycleQueryResponse} from '@core/api/data-access';
import {DateRangeType, ICycleQuery, isICycleQuery} from '@global-data';
import {ToastService} from '@utils';
import dayjs from 'dayjs';
import {catchError, Observable, retry, throwError, timer} from 'rxjs';
import {CycleCyclesService} from './cycle.cycles.service';
import {DocumentCyclesService} from './document.cycles.service';
import {FieldsCyclesService} from './fields.cycles.service';
import {FormsCyclesService} from './forms.cycles.service';
import {ListCyclesService} from './list.cycles.service';
import {TemplateCyclesService} from './template.cycles.service';

@Injectable({
	providedIn: 'root',
})
export class CyclesService extends BackendService {
	public readonly Cycle: CycleCyclesService = new CycleCyclesService(this);
	public readonly Document: DocumentCyclesService = new DocumentCyclesService(this);
	public readonly Fields: FieldsCyclesService = new FieldsCyclesService(this);
	public readonly List: ListCyclesService = new ListCyclesService(this);

	public readonly Forms: FormsCyclesService = new FormsCyclesService(this);
	public readonly Template: TemplateCyclesService = new TemplateCyclesService(this);
	/* -------------------------------------------------- */
	public readonly toastService: ToastService = inject(ToastService);
	public readonly injector: Injector = inject(Injector);
	public readonly authService: AuthService = this.injector.get(AuthService);
	public readonly authStore: AuthStore = inject(AuthStore);
	public readonly activeUserStore: ActiveUserStore = inject(ActiveUserStore);

	constructor() {
		super('cycles');
	}

	public query(queryParams: ICycleQuery | ICycleQueryRequest): Observable<ICycleQueryResponse> {
		const requestParams: ICycleQueryRequest = isICycleQuery(queryParams)
			? {
					query: buildQueryString(queryParams),
					page: queryParams.page,
					size: queryParams.size,
				}
			: queryParams;
		return this.apiService.get<ICycleQueryResponse>(this.getUrl('query'), requestParams).pipe(
			retry({
				count: 3,
				delay: (error, retryCount) => {
					if (error.status === 401) {
						return throwError(() => error);
					}
					console.warn(`Retrying request ${retryCount} times`);
					return timer(1500);
				},
			}),
			catchError(error => {
				console.error('All retry attempts failed or 401 error occurred', error);
				this.toastService.error('Error loading cycles data');
				// Return an empty response object instead of EMPTY to prevent undefined errors
				return throwError(() => new Error('Failed to load cycles data'));
			}),
		);

		/**
		 * Builds a query string from the given query parameters.
		 * @param queryParams
		 */
		function buildQueryString(queryParams: ICycleQuery): string {
			let query = '';

			if (queryParams.query) {
				query = `${queryParams.query}`;
			}

			if (queryParams.listType) {
				const types = queryParams.listType.map((type: CycleListType) => type.charAt(0).toUpperCase() + type.slice(1));
				if (types.length > 0) query += ` type:[${types.join(',')}]`;
			}

			if (queryParams.sortBy) {
				query += ` sortBy:${queryParams.sortBy.sortMarker} sortDesc:${queryParams.sortBy.direction === 'desc'}`;
			}
			const from = queryParams.fromDate || queryParams.fromCreateDate;
			const to = queryParams.toDate || queryParams.toCreateDate;
			if (from && to) {
				switch (queryParams.dateRangeType) {
					case DateRangeType.LastUpdate:
						query += ` fromDate:${dayjs(from).format()} toDate:${dayjs(to).format()}`;
						break;
					case DateRangeType.CreateDate:
						query += ` fromCreateDate:${dayjs(from).format()} toCreateDate:${dayjs(to).format()}`;
						break;
				}
			}

			return query;
		}
	}
}
